import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Building2, Route as RouteIcon, Newspaper, MessageSquareWarning, Plane, Map as MapIcon, Info, Camera, TrendingUp, MessageCircle, BookOpen, Award } from "lucide-react";
import api from "../lib/api";
import { AviationBanner, Spinner, Pill, StatBox, fmtNum, TabBar } from "../components/common";
import TopBoard from "../components/TopBoard";
import AiContentPack from "../components/AiContentPack";
import MapView from "../components/MapView";
import AiAirlineProfile from "../components/AiAirlineProfile";
import AiLoyaltyOverview from "../components/AiLoyaltyOverview";
import NewsFeed from "../components/NewsFeed";
import WikiPanel from "../components/WikiPanel";
import PhotoGallery from "../components/PhotoGallery";
import DiscussionBoard from "../components/DiscussionBoard";
import FeaturedArticle from "../components/FeaturedArticle";
import AiEntityProfile from "../components/AiEntityProfile";
import { pickAirlineBg } from "../lib/assets";

const TABS = [
  { key: "overview", label: "Overview", icon: <Info className="w-4 h-4" /> },
  { key: "photos", label: "Photos", icon: <Camera className="w-4 h-4" /> },
  { key: "articles", label: "Articles", icon: <Newspaper className="w-4 h-4" /> },
  { key: "loyalty", label: "Loyalty & Mileage", icon: <Award className="w-4 h-4" /> },
  { key: "news", label: "News", icon: <Newspaper className="w-4 h-4" /> },
  { key: "trending", label: "Trending", icon: <TrendingUp className="w-4 h-4" /> },
  { key: "complaints", label: "Discussion & Complaints", icon: <MessageSquareWarning className="w-4 h-4" /> },
  { key: "stories", label: "Stories", icon: <BookOpen className="w-4 h-4" /> },
];

export default function AirlineDetail() {
  const { code } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    setA(null); setErr(false);
    const hash = (window.location.hash || "").replace("#", "");
    setTab(TABS.some((t) => t.key === hash) ? hash : "overview");
    api.get(`/airlines/${code}`).then((r) => {
      setA(r.data);
      api.get("/complaints", { params: { entity_type: "airline", entity_id: r.data.id, limit: 5 } })
        .then((c) => setComplaints(c.data.items)).catch(() => {});
    }).catch(() => setErr(true));
  }, [code]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-muted-foreground">Airline not found.</div>;
  if (!a) return <Spinner full />;

  const HDR = pickAirlineBg(a.id || a.name);

  return (
    <div data-testid="airline-detail">
      <TopBoard entity={a} kind="airline"
        badge={a.iata && `${a.iata}${a.icao ? ` · ${a.icao}` : ""} · ${a.active === "Y" ? "Active" : "Inactive"}`}
        title={a.name}
        subtitle={[a.country, a.callsign].filter(Boolean).join(" · ") || null}
      />
      <TabBar tabs={TABS} active={tab} onChange={setTab} attached />
      <section className="relative border-b border-zinc-200">
        <div className="absolute inset-0"><img src={HDR} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-sky-900/80" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14">
          <Link to="/airlines" className="inline-flex items-center gap-1 text-sm text-white/80 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> Airlines</Link>
          <div className="flex items-center gap-2 mb-2">
            {a.iata && <span className="font-mono text-2xl text-blue-400 font-500">{a.iata}</span>}
            {a.active === "Y" ? <Pill tone="green">Active</Pill> : <Pill>Inactive</Pill>}
          </div>
          <h1 className="font-heading font-900 text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white max-w-3xl">{a.name}</h1>
          <div className="flex items-center gap-1.5 text-zinc-200 mt-3 text-lg sm:text-xl font-500"><MapPin className="w-5 h-5" />{a.country || "—"}{a.callsign ? ` · ${a.callsign}` : ""}</div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 -mt-8 relative z-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-300 border border-zinc-300">
        <StatBox value={fmtNum(a.routes_count || 0)} label="Routes" testid="airline-routes-count" />
        <StatBox value={fmtNum(a.hubs?.length || 0)} label="Hubs" />
        <StatBox value={a.icao || "—"} label="ICAO" />
        <StatBox value={a.iata || "—"} label="IATA" />
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        {tab === "overview" && (
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2 space-y-10">
              <AiEntityProfile type="airline" code={a.iata || a.id} name={a.name} />
              <AiContentPack code={a.iata || a.icao || a.id} kind="airline" />
              <FeaturedArticle entityType="airline" entityId={a.id} />
              <div>
                <h2 className="font-heading font-900 text-blue-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><Building2 className="w-6 h-6" /> Primary hubs</h2>
                {a.hubs?.length ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {a.hubs.map((h) => (
                      <Link key={h.id} to={`/airports/${h.iata_code || h.id}`} className="flex items-center justify-between border border-zinc-300 bg-white px-4 py-3 hover:border-primary transition-colors">
                        <span className="text-sm font-medium truncate">{h.name}</span>
                        <span className="font-mono text-xs text-primary">{h.iata_code} · {h.route_count} rts</span>
                      </Link>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No hub data available.</p>}
              </div>

              {a.route_map?.length > 0 && (
                <div>
                  <h2 className="font-heading font-900 text-violet-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><MapIcon className="w-6 h-6" /> Route network map <span className="text-sm font-500 text-muted-foreground">({a.route_map.length} legs)</span></h2>
                  <MapView
                    center={a.hubs?.[0]?.latitude ? [a.hubs[0].latitude, a.hubs[0].longitude] : [20, 0]}
                    zoom={a.hubs?.[0]?.latitude ? 3 : 2} height="420px"
                    lines={a.route_map}
                    markers={(a.hubs || []).filter((h) => h.latitude).map((h) => ({ lat: h.latitude, lon: h.longitude, radius: 5, popup: `${h.name} (${h.iata_code})` }))}
                  />
                </div>
              )}

              <div>
                <h2 className="font-heading font-900 text-rose-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><RouteIcon className="w-6 h-6" /> Route network</h2>
                {a.routes?.length ? (
                  <div className="border border-zinc-300 bg-white divide-y divide-zinc-200 max-h-96 overflow-auto">
                    {a.routes.map((r, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <span className="font-mono"><span className="text-primary">{r.source_airport}</span> → {r.destination_airport}</span>
                        <span className="font-mono text-xs text-muted-foreground">{r.equipment || ""}</span>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">No route data available.</p>}
              </div>

              {a.fleet?.length > 0 && (
                <div>
                  <h2 className="font-heading font-900 text-emerald-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><Plane className="w-6 h-6 -rotate-45" /> Fleet <span className="text-sm font-500 text-muted-foreground">(aircraft types operated)</span></h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {a.fleet.slice(0, 14).map((f) => (
                      <Link key={f.iata} to={`/aircraft/${encodeURIComponent(f.iata)}`} className="flex items-center justify-between border border-zinc-300 bg-white px-4 py-2.5 hover:border-primary transition-colors" data-testid={`airline-fleet-${f.iata}`}>
                        <span className="text-sm font-medium truncate">{f.name}</span>
                        <span className="font-mono text-xs text-muted-foreground shrink-0">{f.route_count} routes</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-8">
              <WikiPanel title={a.name} />
              <div>
                <h3 className="font-heading font-700 flex items-center gap-2 mb-3"><Newspaper className="w-4 h-4 text-primary" /> Latest news</h3>
                <NewsFeed path={`/airlines/${a.id}/news?limit=6`} compact />
              </div>
            </div>
          </div>
        )}

        {tab === "photos" && <PhotoGallery entityType="airline" entityId={a.id} />}

        {tab === "articles" && (
          <div className="space-y-10">
            <AiAirlineProfile code={a.id} seed={a.id || a.name} name={a.name} />
            {a.articles?.length > 0 && (
              <div>
                <h2 className="font-heading font-900 text-blue-600 text-xl sm:text-2xl mb-4 flex items-center gap-2"><Newspaper className="w-6 h-6" /> Featured articles</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {a.articles.map((ar) => (
                    <Link key={ar.slug} to={`/articles/${ar.slug}`} className="border border-zinc-300 bg-white p-5 hover:border-primary transition-colors">
                      <Newspaper className="w-5 h-5 text-primary mb-2" />
                      <div className="font-heading font-700 leading-snug">{ar.title}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "loyalty" && <AiLoyaltyOverview code={a.id} name={a.name} />}

        {tab === "news" && <NewsFeed path={`/airlines/${a.id}/news?limit=18`} />}        {tab === "trending" && (
          <div>
            <div className="flex items-center gap-2 mb-4"><Pill tone="blue">Trending</Pill><span className="text-sm text-muted-foreground">Most talked-about {a.name} headlines right now</span></div>
            <NewsFeed path={`/airlines/${a.id}/news?limit=18`} />
          </div>
        )}

        {tab === "complaints" && (
          <div className="space-y-10">
            <div className="border border-zinc-300 bg-white p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2"><MessageSquareWarning className="w-5 h-5 text-destructive" /><span className="font-700">Have a formal complaint about {a.name}?</span></div>
              <Link to={`/complaints/new?type=airline&id=${a.id}&name=${encodeURIComponent(a.name)}&iata=${a.iata || ""}`} className="px-4 py-2 bg-destructive text-white text-sm font-600 hover:opacity-90" data-testid="airline-file-complaint">File a complaint</Link>
            </div>
            {complaints.length > 0 && (
              <div>
                <h3 className="font-heading font-700 mb-3">Recent complaints</h3>
                <div className="grid md:grid-cols-2 gap-2">
                  {complaints.map((c) => (
                    <Link key={c.complaint_id} to={`/complaints/${c.complaint_id}`} className="block border border-zinc-300 bg-white p-3 hover:border-primary transition-colors">
                      <div className="text-sm font-medium line-clamp-1">{c.title}</div>
                      <div className="text-xs font-mono text-muted-foreground mt-1">▲ {c.upvote_count} · {c.category}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            <DiscussionBoard entityType="airline" entityId={a.id} entityName={a.name} kind="comment" title={`${a.name} discussion board`} />
          </div>
        )}

        {tab === "stories" && <DiscussionBoard entityType="airline" entityId={a.id} entityName={a.name} kind="story" title={`Passenger stories about ${a.name}`} />}
      </div>
    </div>
  );
}
